import React, { useState, useEffect } from 'react';
import { OperationVariables, QueryResult, useLazyQuery } from '@apollo/client';
import {
  TagPicker,
  TagPickerList,
  TagPickerInput,
  TagPickerControl,
  TagPickerProps,
  TagPickerOption,
  TagPickerGroup,
  Tag,
  Field,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import { GET_SEC_REGION_AREAS_BY_SEARCH_INPUT } from '../../../utils/statApi/CompaniesApi';
import { ISecRegionArea } from '../../../utils/types/ISecRegionArea';
import ITag from '../../../utils/types/ITag';
import { convertSecRegionAreaToTag } from '../../../utils/Helpers';

interface IWwAreaPickerProps {
  label?: JSX.Element;
  itemLimit?: number;
  isDisabled: boolean;
  selectedItems: ITag[];
  errorMessage: string;
  setSelectedItems: (secRegion: ITag[]) => void;
}

const WwAreaPicker = ({
  setSelectedItems,
  selectedItems,
  isDisabled,
  errorMessage,
  label,
  itemLimit,
}: IWwAreaPickerProps): JSX.Element => {
  const [loadSecRegionAreas] = useLazyQuery(GET_SEC_REGION_AREAS_BY_SEARCH_INPUT);
  const [availableOptions, setAvailableOptions] = useState<ITag[]>([]);
  const [query, setQuery] = useState('');

  // Convert selectedItems to the format expected by TagPicker v9
  const selectedOptions = (selectedItems || []).map((item) => item.key.toString());

  // Load areas when query changes
  useEffect(() => {
    if (query.length > 0) {
      loadSecRegionAreas({ variables: { keyword: query } }).then(
        (result: QueryResult<{ secRegionAreas: ISecRegionArea[] }, OperationVariables>) => {
          const secRegionAreaList: ISecRegionArea[] = result?.data?.secRegionAreas || [];
          const options = secRegionAreaList.map((secRegionArea) => {
            const tag = convertSecRegionAreaToTag(secRegionArea);
            return {
              key: tag.key.toString(),
              name: tag.name,
            };
          });
          setAvailableOptions(options);
        },
      );
    } else {
      setAvailableOptions([]);
    }
  }, [query, loadSecRegionAreas]);

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (e, data) => {
    if (data.value === 'no-options') {
      return;
    }

    // Check item limit
    if (itemLimit && data.selectedOptions.length > itemLimit) {
      return;
    }

    // Convert selected option keys back to full objects
    const selected = data.selectedOptions
      .map((optionKey) => {
        // Find in current selected items first
        const existing = selectedItems?.find((item) => item.key === optionKey);
        if (existing) return existing;

        // Find in available options
        const available = availableOptions.find((item) => item.key === optionKey);
        return available;
      })
      .filter(Boolean) as ITag[];

    setSelectedItems(selected);
    setQuery('');
  };

  // Filter options to exclude already selected items
  const filteredOptions = availableOptions.filter(
    (option) => !selectedOptions.includes(option.key.toString()),
  );

  const labelText = label || 'Area';

  return (
    <FluentProvider theme={webLightTheme}>
      <Field label={labelText} required validationMessage={errorMessage}>
        <TagPicker
          onOptionSelect={onOptionSelect}
          selectedOptions={selectedOptions}
          disabled={isDisabled}
        >
          <TagPickerControl>
            <TagPickerGroup aria-label="Selected Areas">
              {(selectedItems || []).map((item) => (
                <Tag key={item.key} value={item.key.toString()}>
                  {item.name}
                </Tag>
              ))}
            </TagPickerGroup>
            <TagPickerInput
              aria-label="Search Areas"
              placeholder="Search Areas"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </TagPickerControl>
          <TagPickerList>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <TagPickerOption value={option.key.toString()} key={option.key.toString()}>
                  {option.name}
                </TagPickerOption>
              ))
            ) : (
              <TagPickerOption value="no-options">No areas found</TagPickerOption>
            )}
          </TagPickerList>
        </TagPicker>
      </Field>
    </FluentProvider>
  );
};
export default WwAreaPicker;
